import { useState } from 'react';
import { StanSegmentedControl } from '@/components';
import { FiBarChart2, FiCalendar, FiGrid, FiList, FiSettings, FiUsers } from 'react-icons/fi';
import { MdOutlineViewAgenda, MdOutlineViewDay, MdOutlineViewModule, MdOutlineViewWeek } from 'react-icons/md';

export default function InternalsSegmentedControlPage() {
    // Basic usage examples
    const [viewMode, setViewMode] = useState<string | number>('grid');
    const [timeRange, setTimeRange] = useState<string | number>('week');
    const [alignment, setAlignment] = useState<string | number>('left');
    const [priority, setPriority] = useState<string | number>(2);

    // Size examples
    const [sizeSmall, setSizeSmall] = useState<string | number>('option1');
    const [sizeMedium, setSizeMedium] = useState<string | number>('option1');
    const [sizeLarge, setSizeLarge] = useState<string | number>('option1');

    // Variant examples
    const [variantDefault, setVariantDefault] = useState<string | number>('tab1');
    const [variantFilled, setVariantFilled] = useState<string | number>('tab1');

    // Width examples
    const [fullWidthExample, setFullWidthExample] = useState<string | number>('all');
    const [inlineExample, setInlineExample] = useState<string | number>('private');

    // With icons
    const [layoutView, setLayoutView] = useState<string | number>('day');
    const [navigationTab, setNavigationTab] = useState<string | number>('dashboard');

    // Disabled states
    const [enabledControl, setEnabledControl] = useState<string | number>('edit');
    const [disabledOptionControl, setDisabledOptionControl] = useState<string | number>('draft');

    // Real-world scenarios
    const [reportType, setReportType] = useState<string | number>('summary');
    const [chartType, setChartType] = useState<string | number>('line');
    const [filterPeriod, setFilterPeriod] = useState<string | number>('month');

    return (
        <div className='space-y-8'>
            {/* Introduction Section */}
            <div
                className='rounded-lg p-8 shadow-sm'
                style={{
                    border: '1px solid var(--color-border-subtle)',
                    backgroundColor: 'var(--color-bg-canvas)',
                }}
            >
                <div className='mb-6'>
                    <h2 className='text-2xl font-semibold' style={{ color: 'var(--text-color-dark)' }}>
                        Segmented Control
                    </h2>
                    <p className='mt-2' style={{ color: 'var(--text-color-gray)' }}>
                        A flexible segmented control component for switching between options or views. Supports keyboard
                        navigation (Arrow Left/Right), icons, multiple sizes, and variants.
                    </p>
                </div>

                {/* Basic Examples */}
                <div className='space-y-8'>
                    <div>
                        <h3 className='mb-4 text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                            Basic Usage
                        </h3>
                        <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
                            {/* View Mode Selector */}
                            <div className='space-y-3'>
                                <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                                    View Mode
                                </label>
                                <StanSegmentedControl
                                    name='view-mode'
                                    value={viewMode}
                                    onChange={setViewMode}
                                    options={[
                                        { value: 'grid', label: 'Grid', icon: FiGrid },
                                        { value: 'list', label: 'List', icon: FiList },
                                        { value: 'calendar', label: 'Calendar', icon: FiCalendar },
                                    ]}
                                    aria-label='View mode selector'
                                />
                                <p className='text-xs' style={{ color: 'var(--text-color-gray)' }}>
                                    Selected: {viewMode}
                                </p>
                            </div>

                            {/* Time Range Selector */}
                            <div className='space-y-3'>
                                <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                                    Time Range
                                </label>
                                <StanSegmentedControl
                                    name='time-range'
                                    value={timeRange}
                                    onChange={setTimeRange}
                                    options={[
                                        { value: 'day', label: 'Day' },
                                        { value: 'week', label: 'Week' },
                                        { value: 'month', label: 'Month' },
                                        { value: 'year', label: 'Year' },
                                    ]}
                                />
                                <p className='text-xs' style={{ color: 'var(--text-color-gray)' }}>
                                    Selected: {timeRange}
                                </p>
                            </div>

                            {/* Text Alignment */}
                            <div className='space-y-3'>
                                <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                                    Text Alignment
                                </label>
                                <StanSegmentedControl
                                    name='alignment'
                                    value={alignment}
                                    onChange={setAlignment}
                                    options={[
                                        { value: 'left', label: 'Left' },
                                        { value: 'center', label: 'Center' },
                                        { value: 'right', label: 'Right' },
                                    ]}
                                />
                                <p className='text-xs' style={{ color: 'var(--text-color-gray)' }}>
                                    Selected: {alignment}
                                </p>
                            </div>

                            {/* Priority Selector (Numeric values) */}
                            <div className='space-y-3'>
                                <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                                    Priority Level
                                </label>
                                <StanSegmentedControl
                                    name='priority'
                                    value={priority}
                                    onChange={setPriority}
                                    options={[
                                        { value: 1, label: 'Low' },
                                        { value: 2, label: 'Medium' },
                                        { value: 3, label: 'High' },
                                    ]}
                                />
                                <p className='text-xs' style={{ color: 'var(--text-color-gray)' }}>
                                    Selected: {priority}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Size Variants Section */}
            <div
                className='rounded-lg p-8 shadow-sm'
                style={{
                    border: '1px solid var(--color-border-subtle)',
                    backgroundColor: 'var(--color-bg-canvas)',
                }}
            >
                <h3 className='mb-4 text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                    Size Variants
                </h3>
                <div className='space-y-6'>
                    {/* Small */}
                    <div className='space-y-3'>
                        <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                            Small Size
                        </label>
                        <StanSegmentedControl
                            name='size-small'
                            size='sm'
                            value={sizeSmall}
                            onChange={setSizeSmall}
                            options={[
                                { value: 'option1', label: 'Option 1' },
                                { value: 'option2', label: 'Option 2' },
                                { value: 'option3', label: 'Option 3' },
                            ]}
                        />
                    </div>

                    {/* Medium (Default) */}
                    <div className='space-y-3'>
                        <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                            Medium Size (Default)
                        </label>
                        <StanSegmentedControl
                            name='size-medium'
                            size='md'
                            value={sizeMedium}
                            onChange={setSizeMedium}
                            options={[
                                { value: 'option1', label: 'Option 1' },
                                { value: 'option2', label: 'Option 2' },
                                { value: 'option3', label: 'Option 3' },
                            ]}
                        />
                    </div>

                    {/* Large */}
                    <div className='space-y-3'>
                        <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                            Large Size
                        </label>
                        <StanSegmentedControl
                            name='size-large'
                            size='lg'
                            value={sizeLarge}
                            onChange={setSizeLarge}
                            options={[
                                { value: 'option1', label: 'Option 1' },
                                { value: 'option2', label: 'Option 2' },
                                { value: 'option3', label: 'Option 3' },
                            ]}
                        />
                    </div>
                </div>
            </div>

            {/* Style Variants Section */}
            <div
                className='rounded-lg p-8 shadow-sm'
                style={{
                    border: '1px solid var(--color-border-subtle)',
                    backgroundColor: 'var(--color-bg-canvas)',
                }}
            >
                <h3 className='mb-4 text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                    Style Variants
                </h3>
                <div className='space-y-6'>
                    {/* Default Variant */}
                    <div className='space-y-3'>
                        <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                            Default Variant
                        </label>
                        <StanSegmentedControl
                            name='variant-default'
                            variant='default'
                            value={variantDefault}
                            onChange={setVariantDefault}
                            options={[
                                { value: 'tab1', label: 'Tab 1' },
                                { value: 'tab2', label: 'Tab 2' },
                                { value: 'tab3', label: 'Tab 3' },
                            ]}
                        />
                    </div>

                    {/* Filled Variant */}
                    <div className='space-y-3'>
                        <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                            Filled Variant
                        </label>
                        <StanSegmentedControl
                            name='variant-filled'
                            variant='filled'
                            value={variantFilled}
                            onChange={setVariantFilled}
                            options={[
                                { value: 'tab1', label: 'Tab 1' },
                                { value: 'tab2', label: 'Tab 2' },
                                { value: 'tab3', label: 'Tab 3' },
                            ]}
                        />
                    </div>
                </div>
            </div>

            {/* Width Options Section */}
            <div
                className='rounded-lg p-8 shadow-sm'
                style={{
                    border: '1px solid var(--color-border-subtle)',
                    backgroundColor: 'var(--color-bg-canvas)',
                }}
            >
                <h3 className='mb-4 text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                    Width Options
                </h3>
                <div className='space-y-6'>
                    {/* Full Width */}
                    <div className='space-y-3'>
                        <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                            Full Width (Default)
                        </label>
                        <StanSegmentedControl
                            name='full-width'
                            fullWidth
                            value={fullWidthExample}
                            onChange={setFullWidthExample}
                            options={[
                                { value: 'all', label: 'All Items' },
                                { value: 'active', label: 'Active' },
                                { value: 'archived', label: 'Archived' },
                            ]}
                        />
                    </div>

                    {/* Inline Width */}
                    <div className='space-y-3'>
                        <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                            Inline Width
                        </label>
                        <StanSegmentedControl
                            name='inline-width'
                            fullWidth={false}
                            value={inlineExample}
                            onChange={setInlineExample}
                            options={[
                                { value: 'private', label: 'Private' },
                                { value: 'public', label: 'Public' },
                            ]}
                        />
                    </div>
                </div>
            </div>

            {/* With Icons Section */}
            <div
                className='rounded-lg p-8 shadow-sm'
                style={{
                    border: '1px solid var(--color-border-subtle)',
                    backgroundColor: 'var(--color-bg-canvas)',
                }}
            >
                <h3 className='mb-4 text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                    With Icons
                </h3>
                <div className='space-y-6'>
                    {/* Layout Views */}
                    <div className='space-y-3'>
                        <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                            Layout Views
                        </label>
                        <StanSegmentedControl
                            name='layout-view'
                            value={layoutView}
                            onChange={setLayoutView}
                            options={[
                                { value: 'day', label: 'Day', icon: MdOutlineViewDay },
                                { value: 'week', label: 'Week', icon: MdOutlineViewWeek },
                                { value: 'month', label: 'Month', icon: MdOutlineViewModule },
                                { value: 'agenda', label: 'Agenda', icon: MdOutlineViewAgenda },
                            ]}
                        />
                    </div>

                    {/* Navigation Tabs */}
                    <div className='space-y-3'>
                        <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                            Navigation Tabs
                        </label>
                        <StanSegmentedControl
                            name='navigation-tab'
                            value={navigationTab}
                            onChange={setNavigationTab}
                            size='lg'
                            options={[
                                { value: 'dashboard', label: 'Dashboard', icon: FiBarChart2 },
                                { value: 'team', label: 'Team', icon: FiUsers },
                                { value: 'settings', label: 'Settings', icon: FiSettings },
                            ]}
                        />
                    </div>
                </div>
            </div>

            {/* Disabled States Section */}
            <div
                className='rounded-lg p-8 shadow-sm'
                style={{
                    border: '1px solid var(--color-border-subtle)',
                    backgroundColor: 'var(--color-bg-canvas)',
                }}
            >
                <h3 className='mb-4 text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                    Disabled States
                </h3>
                <div className='space-y-6'>
                    {/* Fully Disabled */}
                    <div className='space-y-3'>
                        <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                            Fully Disabled Control
                        </label>
                        <StanSegmentedControl
                            name='fully-disabled'
                            disabled
                            value={enabledControl}
                            onChange={setEnabledControl}
                            options={[
                                { value: 'edit', label: 'Edit' },
                                { value: 'view', label: 'View' },
                                { value: 'delete', label: 'Delete' },
                            ]}
                        />
                    </div>

                    {/* Individual Disabled Options */}
                    <div className='space-y-3'>
                        <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                            Individual Disabled Options
                        </label>
                        <StanSegmentedControl
                            name='disabled-options'
                            value={disabledOptionControl}
                            onChange={setDisabledOptionControl}
                            options={[
                                { value: 'draft', label: 'Draft' },
                                { value: 'published', label: 'Published', disabled: true },
                                { value: 'archived', label: 'Archived' },
                            ]}
                        />
                        <p className='text-xs' style={{ color: 'var(--text-color-gray)' }}>
                            The &quot;Published&quot; option is disabled
                        </p>
                    </div>
                </div>
            </div>

            {/* Real-World Scenarios Section */}
            <div
                className='rounded-lg p-8 shadow-sm'
                style={{
                    border: '1px solid var(--color-border-subtle)',
                    backgroundColor: 'var(--color-bg-canvas)',
                }}
            >
                <h3 className='mb-4 text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                    Real-World Scenarios
                </h3>
                <div className='space-y-8'>
                    {/* Report Type Selector */}
                    <div className='space-y-3'>
                        <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                            Report Type
                        </label>
                        <StanSegmentedControl
                            name='report-type'
                            value={reportType}
                            onChange={setReportType}
                            options={[
                                { value: 'summary', label: 'Summary' },
                                { value: 'detailed', label: 'Detailed' },
                                { value: 'export', label: 'Export' },
                            ]}
                        />
                        <div
                            className='rounded-md p-4'
                            style={{
                                backgroundColor: 'var(--color-info-subtle)',
                                border: '1px solid var(--color-border-info)',
                            }}
                        >
                            <p className='text-sm' style={{ color: 'var(--text-color-dark)' }}>
                                {reportType === 'summary' && 'Showing summary view with key metrics and highlights'}
                                {reportType === 'detailed' &&
                                    'Showing detailed view with comprehensive data and analytics'}
                                {reportType === 'export' && 'Prepare to export data in various formats'}
                            </p>
                        </div>
                    </div>

                    {/* Chart Type Selector */}
                    <div className='space-y-3'>
                        <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                            Chart Type
                        </label>
                        <StanSegmentedControl
                            name='chart-type'
                            value={chartType}
                            onChange={setChartType}
                            size='lg'
                            options={[
                                { value: 'line', label: 'Line Chart' },
                                { value: 'bar', label: 'Bar Chart' },
                                { value: 'pie', label: 'Pie Chart' },
                                { value: 'area', label: 'Area Chart' },
                            ]}
                        />
                        <div
                            className='rounded-md p-4'
                            style={{
                                backgroundColor: 'var(--color-success-subtle)',
                                border: '1px solid var(--color-border-success)',
                            }}
                        >
                            <p className='text-sm' style={{ color: 'var(--text-color-dark)' }}>
                                Selected chart type: <strong>{chartType}</strong>
                            </p>
                        </div>
                    </div>

                    {/* Filter Period */}
                    <div className='space-y-3'>
                        <label className='text-sm font-medium' style={{ color: 'var(--text-color-dark)' }}>
                            Filter Period
                        </label>
                        <StanSegmentedControl
                            name='filter-period'
                            value={filterPeriod}
                            onChange={setFilterPeriod}
                            fullWidth={false}
                            options={[
                                { value: 'week', label: 'Week' },
                                { value: 'month', label: 'Month' },
                                { value: 'quarter', label: 'Quarter' },
                                { value: 'year', label: 'Year' },
                            ]}
                        />
                        <p className='text-xs' style={{ color: 'var(--text-color-gray)' }}>
                            Filtering data for the last {filterPeriod}
                        </p>
                    </div>
                </div>
            </div>

            {/* Keyboard Navigation Info */}
            <div
                className='rounded-lg p-6'
                style={{
                    border: '1px solid var(--color-border-info)',
                    backgroundColor: 'var(--color-info-subtle)',
                }}
            >
                <h4 className='mb-2 text-sm font-semibold' style={{ color: 'var(--text-color-dark)' }}>
                    Keyboard Navigation
                </h4>
                <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                    Use <kbd className='rounded bg-white px-2 py-1 text-xs'>←</kbd> and{' '}
                    <kbd className='rounded bg-white px-2 py-1 text-xs'>→</kbd> arrow keys to navigate between segments.
                    The component is fully keyboard accessible and follows ARIA best practices.
                </p>
            </div>
        </div>
    );
}
